import { fullscreen as fullscreenService } from '@gig/magic-gameplay/dist/services';

const CAPTAIN_UP_ACTIVITY_TYPE = 'activities';
const CAPTAIN_UP_ACHIEVE_ACTIVITY_NAME = 'achieve';
const CAPTAIN_UP_SUBSCRIBE_RETRY_DELAY = 500;
const CAPTAIN_UP_SUBSCRIBE_MAX_RETRY_ATTEMPTS = 60;

interface ICaptainUpWidgetSubscriptionApi {
    subscribe?: (callback: CaptainUpSubscriptionCallback) => void;
}

interface ICaptainUpBadgeActivity {
    name?: string;
}

type CaptainUpSubscriptionCallback = (type: string, entity: unknown) => void;

// Exits gameplay fullscreen when Captain Up reports a reward achievement,
// so the reward popup is visible to the player.
class CaptainUpRewardService {
    private retryTimeout: number | null = null;
    private retryAttempt: number = 0;
    private isSubscriptionRegistered: boolean = false;
    private activeSessionId: number = 0;

    start(enabled: boolean): () => void {
        this.stop();

        const sessionId = this.activeSessionId;
        this.retryAttempt = 0;
        this.isSubscriptionRegistered = false;

        if (!enabled) {
            return () => this.stop(sessionId);
        }

        this.registerActivitySubscription(sessionId);

        return () => this.stop(sessionId);
    }

    private stop(sessionId?: number): void {
        if (sessionId && sessionId !== this.activeSessionId) {
            return;
        }

        this.activeSessionId += 1;
        this.clearRetryTimeout();
        this.isSubscriptionRegistered = false;
    }

    private registerActivitySubscription(sessionId: number): void {
        if (!this.isActiveSession(sessionId) || this.isSubscriptionRegistered) {
            return;
        }

        const captainUpWidget = this.getCaptainUpWidget();
        if (typeof captainUpWidget?.subscribe !== 'function') {
            this.scheduleSubscriptionRetry(sessionId);
            return;
        }

        captainUpWidget.subscribe((type, entity) => {
            if (!this.isActiveSession(sessionId)) {
                return;
            }

            this.handleActivityEvent(type, entity);
        });
        this.isSubscriptionRegistered = true;
        this.clearRetryTimeout();
    }

    private scheduleSubscriptionRetry(sessionId: number): void {
        if (
            this.retryTimeout !== null ||
            this.isSubscriptionRegistered ||
            !this.isActiveSession(sessionId)
        ) {
            return;
        }

        if (this.retryAttempt >= CAPTAIN_UP_SUBSCRIBE_MAX_RETRY_ATTEMPTS) {
            return;
        }

        this.retryTimeout = window.setTimeout(() => {
            this.retryTimeout = null;
            this.retryAttempt += 1;
            this.registerActivitySubscription(sessionId);
        }, CAPTAIN_UP_SUBSCRIBE_RETRY_DELAY);
    }

    private clearRetryTimeout(): void {
        if (this.retryTimeout === null) {
            return;
        }

        window.clearTimeout(this.retryTimeout);
        this.retryTimeout = null;
    }

    private handleActivityEvent(type: string, entity: unknown): void {
        if (!this.hasAchievedBadgeActivity(type, entity)) {
            return;
        }

        this.exitFullscreenForAchievement();
    }

    private exitFullscreenForAchievement(): void {
        if (!fullscreenService.isFullscreen()) {
            return;
        }

        fullscreenService.exit();
    }

    private hasAchievedBadgeActivity(type: string, entity: unknown): boolean {
        if (type !== CAPTAIN_UP_ACTIVITY_TYPE) {
            return false;
        }

        if (!Array.isArray(entity)) {
            return false;
        }

        return entity.some((activity) =>
            this.isAchievedBadgeActivity(activity),
        );
    }

    private isAchievedBadgeActivity(
        activity: unknown,
    ): activity is ICaptainUpBadgeActivity {
        return (
            !!activity &&
            typeof activity === 'object' &&
            (activity as ICaptainUpBadgeActivity).name ===
                CAPTAIN_UP_ACHIEVE_ACTIVITY_NAME
        );
    }

    private isActiveSession(sessionId: number): boolean {
        return sessionId === this.activeSessionId;
    }

    private getCaptainUpWidget(): ICaptainUpWidgetSubscriptionApi | null {
        return (
            (
                window as Window & {
                    _CaptainUpWidget?: ICaptainUpWidgetSubscriptionApi;
                }
            )._CaptainUpWidget || null
        );
    }
}

export const captainUpRewardService = new CaptainUpRewardService();
